/**
 * The shva na mark, moved from above the letter to underneath it.
 *
 * Metzuda marks a shva na by writing an ordinary shva and then U+0592, an
 * accent that draws three dots above the letter. oprFrank has no glyph for
 * U+0592, so the browser rendered that one mark from whatever system font it
 * found, in the middle of the word, and the line came apart.
 *
 * The mark belongs at U+05C8, HEBREW POINT HEAVY SHEVA, which the Unicode
 * Technical Committee assigned provisionally in 2025. It cannot be used yet,
 * and the reason is not the font. A browser decides what is a combining mark
 * from the Unicode character database, so an unassigned code point is treated
 * as an ordinary character and never attached to its letter. The same glyph,
 * in the same font, with the same anchors, was measured at both:
 *
 *     alef + U+05B0 (shva, assigned)          mark sits  +4.0px from the letter
 *     alef + U+05C5 (our glyph, assigned)     mark sits  +4.0px
 *     alef + U+05C8 (our glyph, unassigned)   mark sits -71.0px, unattached
 *
 * So the glyph sits at U+05C5 for now: assigned, a below-the-letter mark, and
 * absent from the Siddur. It appears once in the whole corpus, in Ketuvim,
 * which is why the glyph lives in a Siddur-only font rather than in the shared
 * oprFrank. Nothing outside the Siddur reader can be touched by it.
 *
 * When Unicode 18.0 ships and browsers carry U+05C8, change this constant and
 * CODEPOINT in build-siddur-font.py together, and the separate family can go.
 */
export const HEAVY_SHEVA = '\u05C5';
/**
 * Shva, then U+0592, with any combining marks allowed in between.
 *
 * Every one of the 2553 occurrences in the Siddur text has a shva before the
 * accent: 2541 with nothing between them, and twelve with a dagesh or meteg
 * written in between. Whatever sits in the middle is kept, because it is a
 * real part of the word.
 *
 * Two things here are deliberate. The middle group matches any run rather
 * than one optional mark, because the same word can reach us with its dagesh
 * and meteg in either order, and the narrow version silently declined to fire
 * on the other one: the U+0592 it left behind was then drawn by a fallback
 * font, sitting visibly off the letter. And the marks are written as escapes
 * rather than as themselves, because a combining mark in source is invisible
 * and the next person to edit this line cannot see what they are moving.
 */
const SHVA_NA = /\u05B0([\u0591-\u05C7]*)\u0592/g;

/**
 * A shva on the first letter of a word.
 *
 * Metzuda leaves this one to the reader: of the 2533 marked words in the
 * Siddur file, none has its mark on the first letter. A shva at the head of a
 * word is na by the first rule every child learns, so it is marked here, on
 * top of what Metzuda marks. The one exception is the word שְׁתַּיִם and its
 * construct שְׁתֵּי, where the shva is nach; both start shin, shva, tav with a
 * dagesh, and nothing else in the Siddur does.
 *
 * The first group is what precedes the word: the start of the text or any
 * character that is not a Hebrew letter or mark. The second is the letter
 * with whatever marks sit before its shva (a dagesh, a shin dot), kept as
 * they are. The shva is replaced in place, so a dagesh or shin dot written
 * after it stays after it.
 */
// The combining marks, and only those: the maqaf (U+05BE), paseq (U+05C0) and
// sof pasuq (U+05C3) sit in the same block and are word boundaries, not marks.
const MARKS = '\u0591-\u05BD\u05BF\u05C1\u05C2\u05C4\u05C5\u05C7';
// The class deliberately holds combining marks: that is the point of it.
// eslint-disable-next-line no-misleading-character-class
const WORD_INITIAL_SHVA = new RegExp(`(^|[^\u05D0-\u05EA${MARKS}])([\u05D0-\u05EA][${MARKS}]*?)\u05B0`, 'g');
const SHTAYIM = new RegExp(`^ש[${MARKS}]*ת[${MARKS}]*\u05BC`);

export function toHeavySheva(text: string): string {
  return text
    .replace(SHVA_NA, `$1${HEAVY_SHEVA}`)
    .replace(WORD_INITIAL_SHVA, (match, before, letter, offset: number) =>
      SHTAYIM.test(text.slice(offset + before.length)) ? match : `${before}${letter}${HEAVY_SHEVA}`
    );
}

/**
 * Metzuda's own mark for ריווח בין הדבקים: a small circle (U+05AF, the masora
 * circle) over the letter where two words must be kept apart, בְּכָל֯־לְבָבְךָ
 * in the Shema. 570 of them in the Siddur file. oprFrank has no glyph for
 * it, so the browser drew it from a fallback font at the wrong size, and it
 * is not something a reading screen should show anyway: the app tells the
 * reader about a join by colouring the word, not by a mark on the page.
 */
const SEPARATION_MARK = /\u05AF/g;

export function stripSeparationMarks(text: string): string {
  return text.replace(SEPARATION_MARK, '');
}
